import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { offers, comparisonRows } from '../../mock';
import { Wallet, ShieldCheck, TrendingUp, Check, X, ArrowRight, Sparkles, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { publicApi } from '../../lib/api';
import { getSessionId } from '../../lib/userParams';

const iconMap = { Wallet, ShieldCheck, TrendingUp };

export default function OffersAndPrime() {
  return (
    <>
      <PremiumOffers />
      <Comparison />
      <PrimeList />
    </>
  );
}

function PremiumOffers() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-xs font-semibold text-amber-800 uppercase tracking-wider mb-4">Exclusive Offers</div>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Unlock Premium Financial Solutions</h2>
          <p className="mt-4 text-slate-600">Discover our comprehensive suite of financial services designed to help you achieve your financial goals.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {offers.map((o, i) => {
            const Icon = iconMap[o.icon];
            return (
              <div key={o.title} className="relative rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 fadeInUp" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="absolute top-4 right-4">
                  <div className="px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-bold shadow">{o.badge}</div>
                </div>
                <div className="p-7">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="mt-5 font-serif-display text-2xl font-bold text-slate-900">{o.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{o.desc}</p>
                  <div className="mt-5 pt-5 border-t border-slate-100 flex items-baseline justify-between">
                    <span className="text-xs text-slate-500">{o.highlight}</span>
                    <span className="font-serif-display text-2xl font-bold text-emerald-700">{o.highlightValue}</span>
                  </div>
                  <ul className="mt-5 space-y-2">
                    {o.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-slate-500">{o.note}</p>
                  <Link to={o.to}>
                    <Button className="w-full mt-5 h-11 bg-slate-900 hover:bg-emerald-700 text-white group">
                      Learn More <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const Cell = ({ v }) => {
    if (v === true) return <Check className="w-5 h-5 text-emerald-600 mx-auto" />;
    if (v === false) return <X className="w-5 h-5 text-slate-300 mx-auto" />;
    return <span className="text-slate-700">{v}</span>;
  };
  return (
    <section className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <h3 className="font-serif-display text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10">Compare Our Services</h3>
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="grid grid-cols-4 bg-slate-900 text-white text-xs sm:text-sm">
                <div className="p-3 sm:p-5 font-semibold">Features</div>
                <div className="p-3 sm:p-5 font-semibold text-center border-l border-slate-800">Personal Loans</div>
                <div className="p-3 sm:p-5 font-semibold text-center border-l border-slate-800">Credit Protection</div>
                <div className="p-3 sm:p-5 font-semibold text-center border-l border-slate-800">Credit Building</div>
              </div>
              {comparisonRows.map((r, i) => (
                <div key={r.feature} className={`grid grid-cols-4 text-xs sm:text-sm ${i % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                  <div className="p-3 sm:p-5 font-medium text-slate-800">{r.feature}</div>
                  <div className="p-3 sm:p-5 text-center border-l border-slate-100"><Cell v={r.loans} /></div>
                  <div className="p-3 sm:p-5 text-center border-l border-slate-100"><Cell v={r.protect} /></div>
                  <div className="p-3 sm:p-5 text-center border-l border-slate-100"><Cell v={r.build} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 text-center">
          <Link to="/loan">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">Find Your Perfect Financial Solution</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function PrimeList() {
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const { toast } = useToast();

  const [time, setTime] = useState({ d: 2, h: 12, m: 45, s: 30 });
  useEffect(() => {
    const id = setInterval(() => {
      setTime((t) => {
        let { d, h, m, s } = t;
        s -= 1;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 23; d -= 1; }
        if (d < 0) return { d: 2, h: 12, m: 45, s: 30 };
        return { d, h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast({ title: 'Please enter your email' });
    if (!agree) return toast({ title: 'Please agree to receive emails' });
    try {
      await publicApi.submitForm('newsletter', { email, source: 'home_prime_list', sessionId: getSessionId() });
      toast({ title: '🎉 Welcome to the Prime List!', description: 'Check your inbox for exclusive offers.' });
      setEmail('');
      setAgree(false);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    }
  };

  const box = (label, value) => (
    <div className="bg-white/10 border border-white/20 rounded-lg backdrop-blur px-3 py-2.5 min-w-[70px] text-center">
      <div className="font-serif-display text-2xl font-bold text-white tabular-nums">{String(value).padStart(2, '0')}</div>
      <div className="text-[10px] text-emerald-100 uppercase tracking-widest">{label}</div>
    </div>
  );

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden bg-slate-950">
      <div className="absolute inset-0 bg-dark-hero" />
      <div className="absolute inset-0 grid-pattern opacity-25" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-xs font-semibold text-amber-300 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Join VIP List
          </div>
          <h2 className="font-serif-display mt-5 text-3xl md:text-4xl lg:text-5xl text-white font-bold leading-tight">Subscribe to Our Prime List</h2>
          <p className="mt-4 text-slate-300">Get exclusive access to our best recommended financial offers, tips, and resources delivered directly to your inbox.</p>

          <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold uppercase tracking-widest"><Sparkles className="w-4 h-4" /> Limited Time Offer!</div>
            <p className="text-white mt-2">Join our Prime List for exclusive financial insights and offers</p>
            <p className="mt-4 text-xs text-slate-400 uppercase tracking-widest">Offer Expires In:</p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {box('Days', time.d)}{box('Hours', time.h)}{box('Mins', time.m)}{box('Secs', time.s)}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-7 lg:p-8 shadow-2xl">
          <h3 className="font-serif-display text-2xl font-bold text-slate-900">Prime List Subscribers Receive:</h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {[
              'Exclusive financial offers',
              'Early access to new services',
              'Monthly credit tips from experts',
              'Personalized loan notifications',
              'Quarterly market trend reports',
              'Priority customer support'
            ].map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
              </div>
            ))}
          </div>
          <form onSubmit={handleSubscribe} className="mt-6 space-y-3">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9 h-11"
                required
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={setAgree} className="mt-0.5" />
              <span>I agree to receive promotional emails and understand I can unsubscribe at any time.</span>
            </label>
            <Button type="submit" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white">
              Join Prime List – Get Exclusive Updates
            </Button>
            <div className="flex items-center justify-center gap-4 text-[10px] text-slate-400 uppercase tracking-widest pt-1">
              <span>256-bit Secure</span><span>•</span><span>100% Privacy</span><span>•</span><span>No CC Required</span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
